import moment from "moment";
import { BtmCarrier } from "./btm-carrier";
import { BtmReturnReportedProduct } from "./btm-return-reported-product";
import { BtmReturnInspectionProduct, IBtmReturnInspectionProduct } from "./btm-return-inspection-product";
import { Guid } from "guid-typescript";
import { BtmShippingInfo, IBtmShippingInfoDto } from "./btm-shipping-info";
import { BtmReturnRequestStatus, BuildBtmReturnRequestStatusFromString } from "./btm-return-request-status";
import { BtmReturnRequestType, BuildBtmReturnRequestTypeFromString } from "./btm-return-request-type";
import { BuildBtmReturnRequestCategoryFromString } from "./btm-return-request-category";
import { BtmReturnRequestServiceWindow, BuildBtmReturnRequestServiceWindowFromString } from "./btm-return-request-service-window";

export interface IBtmReturnRequestDto {
    demandDeclaration: IBaseBtmReturnRequestDeclarationDto;
    returnRequest: IBaseBtmReturnRequestDetailsDto;
    deliveryOrderProducts: IBaseBtmReturnRequestDeliveryOrderProductDto[];
}

interface IBaseBtmReturnRequestDeclarationDto {
    declarationId: number;
    operationCode: string;
    orderId: string;
    parcelNumber: string;
    items: IBaseBtmReturnRequestProductDto[];
    returnAddress: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
    };
    insertDate: string;
}

interface IBaseBtmReturnRequestProductDto {
    id: number;
    name: string;
    declaredQty: number;
    returnReason: string;
}

export interface IBaseBtmReturnRequestDetailsDto {
    id: string;
    status: string;
    refundStatus: string;
    returnType: string;
    returnCategory: string;
    carrier?: string;
    isBulkyCarrier?: boolean;
    appointmentRequested?: boolean;
    pickupDate?: string;
    serviceWindow?: string;
    shippingInfo: IBtmShippingInfoDto[];
    inspectionItems: IBtmReturnInspectionProduct[];
}

export interface IBaseBtmReturnRequestDeliveryOrderProductDto {
    id: number;
    productName: string;
    productId: number;
    quantity: number;
}

export class BtmReturnRequest {
    private readonly _id: Guid;
    private readonly _declarationId: number;
    private readonly _operationCode: string;
    private readonly _orderId: string;
    private readonly _parcelNumber: string;
    private readonly _customerFirstName: string;
    private readonly _customerLastName: string;
    private readonly _customerPhoneNumber: string;
    private readonly _declaredProducts: BtmReturnReportedProduct[];
    private readonly _carrier: string | undefined;
    private readonly _isBulkyCarrier: boolean | undefined;
    private readonly _pickupDate: moment.Moment | undefined;
    private readonly _serviceWindow: BtmReturnRequestServiceWindow | undefined;
    private readonly _isAppointmentRequested: boolean;
    private readonly _shippingInfo: BtmShippingInfo[];
    private readonly _category: string;
    private readonly _type: BtmReturnRequestType;
    protected _status: BtmReturnRequestStatus;
    private readonly _deliveryOrderContents: IBaseBtmReturnRequestDeliveryOrderProductDto[];
    private readonly _inspectionResult: BtmReturnInspectionProduct[] = [];
    private readonly _insertDate: moment.Moment;

    constructor(data: IBtmReturnRequestDto) {
        this._id = Guid.parse(data.returnRequest.id);
        this._declarationId = data.demandDeclaration.declarationId;
        this._operationCode = data.demandDeclaration.operationCode;
        this._orderId = data.demandDeclaration.orderId;
        this._parcelNumber = data.demandDeclaration.parcelNumber;
        this._customerFirstName = data.demandDeclaration.returnAddress.firstName;
        this._customerLastName = data.demandDeclaration.returnAddress.lastName;
        this._customerPhoneNumber = data.demandDeclaration.returnAddress.phoneNumber;
        this._declaredProducts = data.demandDeclaration.items.map(item =>
            new BtmReturnReportedProduct(item.id, item.name, item.declaredQty, item.returnReason));
        this._carrier = data.returnRequest.carrier;
        this._isBulkyCarrier = data.returnRequest.isBulkyCarrier ?? false;
        this._pickupDate = data.returnRequest.pickupDate ?
            moment(data.returnRequest.pickupDate) : undefined;
        this._serviceWindow = data.returnRequest.serviceWindow ?
            BuildBtmReturnRequestServiceWindowFromString(data.returnRequest.serviceWindow) : undefined;
        this._isAppointmentRequested = data.returnRequest.appointmentRequested ?? false;
        this._shippingInfo = data.returnRequest.shippingInfo.map(shipping => new BtmShippingInfo(shipping));
        this._type = BuildBtmReturnRequestTypeFromString(data.returnRequest.returnType);
        this._category = BuildBtmReturnRequestCategoryFromString(data.returnRequest.returnCategory);
        this._status = BuildBtmReturnRequestStatusFromString(data.returnRequest.status);
        this._deliveryOrderContents = data.deliveryOrderProducts;
        this._inspectionResult = (data.returnRequest.inspectionItems ?? []).map(res => BtmReturnInspectionProduct.fromDataObject(res));
        this._insertDate = moment(data.demandDeclaration.insertDate);
    }

    get id() {
        return this._id;
    }

    get declarationId() {
        return this._declarationId;
    }

    get operationCode() {
        return this._operationCode;
    }

    get orderId() {
        return this._orderId;
    }

    get parcelNumber(): string {
        return this._parcelNumber;
    }

    get customerName() {
        return `${this._customerFirstName} ${this._customerLastName}`;
    }

    get customerPhoneNumber() {
        return this._customerPhoneNumber;
    }

    get declaredProducts(): BtmReturnReportedProduct[] {
        return this._declaredProducts;
    }

    get carrier() {
        return this._carrier;
    }

    get isBulkyCarrier() {
        return this._isBulkyCarrier;
    }

    get pickupDate() {
        return this._pickupDate;
    }

    get serviceWindow() {
        return this._serviceWindow;
    }

    get isAppointmentRequested() {
        return this._isAppointmentRequested;
    }

    get shippingInfo() {
        return this._shippingInfo;
    }

    get category() {
        return this._category;
    }

    get type() {
        return this._type;
    }

    get status() {
        return this._status;
    }

    get isFinalized() {
        return this._status === "Accepted" || this._status === "AutomaticallyAccepted" || this._status === "Rejected";
    }

    get deliveryOrderContents() {
        return this._deliveryOrderContents;
    }

    get inspectionResult() {
        return this._inspectionResult;
    }

    get insertDate(): moment.Moment {
        return this._insertDate;
    }

    public setCarrier(carrier: BtmCarrier | undefined) {
        const data = this.toDto();

        return new BtmReturnRequest({
            ...data,
            returnRequest: {
                ...data.returnRequest,
                carrier: carrier?.name,
                isBulkyCarrier: carrier?.isBulky,
            },
        });
    }

    public setShippingInfo(shippingInfo: BtmShippingInfo[]) {
        const data = this.toDto();

        return new BtmReturnRequest({
            ...this.toDto(),
            returnRequest: {
                ...data.returnRequest,
                shippingInfo: shippingInfo.map(shipping => shipping.toDto()),
            },
        });
    }

    public setAppointment(pickupDate: moment.Moment | undefined, serviceWindow?: string,
                          isAppointmentRequested?: boolean) {
        const data = this.toDto();

        return new BtmReturnRequest({
            ...data,
            returnRequest: {
                ...data.returnRequest,
                pickupDate: pickupDate?.toISOString(),
                serviceWindow,
                appointmentRequested: isAppointmentRequested,
            },
        });
    }

    public toDto() {
        return {
            demandDeclaration: {
                declarationId: this._declarationId,
                operationCode: this._operationCode,
                orderId: this._orderId,
                parcelNumber: this._parcelNumber,
                items: this._declaredProducts.map(product => ({
                    id: product.id,
                    name: product.name,
                    declaredQty: product.quantity,
                    returnReason: product.returnReason,
                })),
                returnAddress: {
                    firstName: this._customerFirstName,
                    lastName: this._customerLastName,
                    phoneNumber: this._customerPhoneNumber,
                },
                insertDate: this._insertDate.toISOString(),
            },
            returnRequest: {
                id: this._id.toString(),
                declarationId: this._declarationId,
                refundStatus: this._status,
                returnType: this._type.toString(),
                returnCategory: this._category.toString(),
                status: this._status.toString(),
                carrier: this._carrier,
                isBulkyCarrier: this._isBulkyCarrier,
                pickupDate: this._pickupDate ? this._pickupDate.toISOString() : undefined,
                serviceWindow: this._serviceWindow,
                appointmentRequested: this._isAppointmentRequested,
                shippingInfo: this._shippingInfo,
                inspectionItems: this._inspectionResult.map(res => res.toDto()),
            },
            deliveryOrderProducts: [],
        } as IBtmReturnRequestDto;
    }
}
